import { useEffect, useState } from 'react';
import { Archive, ArchiveRestore, DollarSign, Info, Link2 } from 'lucide-react';
import { Button, Input, Modal, Select } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { countryOptions } from '../../lib/countries';
import {
  useCreatePerson,
  useSetPersonArchived,
  useSetPersonAddress,
  useSetTimePolicy,
  useUpdatePerson,
  type AccountMember,
  type Person,
  type PersonAddress,
  type PersonFormValues,
  type PersonType,
  type TimesheetCadence,
} from './api';
import { Avatar } from './components';
import { AccountPicker, LinkAccountList, TimePolicyFields } from './time-policy';

interface PersonModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Existing person → edit mode; undefined → create mode. */
  person?: Person;
  /** Called with the saved person's id after a successful save/archive. */
  onSaved?: (person: Person) => void;
}

interface FormState {
  name: string;
  email: string;
  type: string;
  costModel: string;
  hourlyCost: string;
  flatMonthlyCost: string;
  defaultBillRate: string;
  weeklyCapacityHours: string;
  tracksTime: boolean;
  timesheetCadence: TimesheetCadence;
  timesheetRequired: boolean;
  startDate: string;
  endDate: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
}

function toFormState(person?: Person): FormState {
  return {
    name: person?.name ?? '',
    email: person?.email ?? '',
    type: person?.type ?? 'STAFF',
    costModel: person?.costModel ?? 'HOURLY',
    hourlyCost: person?.hourlyCost != null ? String(person.hourlyCost) : '',
    flatMonthlyCost: person?.flatMonthlyCost != null ? String(person.flatMonthlyCost) : '',
    defaultBillRate: person?.defaultBillRate != null ? String(person.defaultBillRate) : '',
    weeklyCapacityHours:
      person?.weeklyCapacityHours != null ? String(person.weeklyCapacityHours) : person ? '' : '40',
    tracksTime: person?.tracksTime ?? false,
    timesheetCadence: person?.timesheetCadence ?? 'NEVER',
    timesheetRequired: person?.timesheetRequired ?? true,
    startDate: person?.startDate ?? '',
    endDate: person?.endDate ?? '',
    addressLine1: person?.address?.line1 ?? '',
    addressLine2: person?.address?.line2 ?? '',
    city: person?.address?.city ?? '',
    region: person?.address?.region ?? '',
    postalCode: person?.address?.postalCode ?? '',
    country: person?.address?.country ?? '',
  };
}

/** The form's address as the API's shape — blanks are null. */
function addressFrom(form: FormState): PersonAddress {
  const v = (s: string) => s.trim() || null;
  return {
    line1: v(form.addressLine1),
    line2: v(form.addressLine2),
    city: v(form.city),
    region: v(form.region),
    postalCode: v(form.postalCode),
    country: v(form.country),
  };
}

const sameAddress = (a: PersonAddress, b: PersonAddress | undefined): boolean =>
  !!b &&
  a.line1 === b.line1 &&
  a.line2 === b.line2 &&
  a.city === b.city &&
  a.region === b.region &&
  a.postalCode === b.postalCode &&
  a.country === b.country;

function parseMoney(value: string): number | null {
  if (!value.trim()) return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function PersonModal({ open, onOpenChange, person, onSaved }: PersonModalProps) {
  const [form, setForm] = useState<FormState>(() => toFormState(person));
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const create = useCreatePerson();
  const update = useUpdatePerson(person?.id ?? '');
  const archive = useSetPersonArchived();
  const setTimePolicy = useSetTimePolicy();
  const setAddress = useSetPersonAddress();
  const saving = create.isPending || update.isPending || setTimePolicy.isPending || setAddress.isPending;

  /** Create mode: the account the new person comes from. Null = step one. */
  const [member, setMember] = useState<AccountMember | null>(null);
  /** Edit mode: an account linked from inside this form, before the refetch lands. */
  const [linkedHere, setLinkedHere] = useState(false);
  const [linking, setLinking] = useState(false);
  const hasAccount = person ? person.hasSolidAccount || linkedHere : true;

  // Reset the form each time the modal opens (or the target person changes).
  // ⚠ Keyed on the ID, not the object: linking an account from inside the form
  // refetches the person, and resetting on that would wipe what was typed.
  useEffect(() => {
    if (open) {
      setForm(toFormState(person));
      setErrors({});
      setServerError(null);
      setMember(null);
      setLinkedHere(false);
      setLinking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, person?.id]);

  const pickMember = (m: AccountMember) => {
    setMember(m);
    setForm((f) => ({ ...f, name: m.name, email: m.email }));
    setErrors({});
    setServerError(null);
  };

  const set = (field: keyof FormState) => (value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (person) {
      if (!form.name.trim()) next.name = 'Name is required';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = 'Enter a valid email';
    }
    if (form.costModel === 'HOURLY' && parseMoney(form.hourlyCost) == null) {
      next.hourlyCost = 'Hourly cost is required';
    }
    if (form.costModel === 'FLAT_MONTHLY' && parseMoney(form.flatMonthlyCost) == null) {
      next.flatMonthlyCost = 'Monthly cost is required';
    }
    if (form.defaultBillRate.trim() && parseMoney(form.defaultBillRate) == null) {
      next.defaultBillRate = 'Enter a valid amount';
    }
    if (form.weeklyCapacityHours.trim()) {
      const capacity = Number(form.weeklyCapacityHours);
      if (!Number.isFinite(capacity) || capacity < 0 || capacity > 168) {
        next.weeklyCapacityHours = 'Enter 0–168 hours';
      }
    }
    if (form.startDate && form.endDate && form.endDate < form.startDate) {
      next.endDate = 'End date is before start date';
    }
    // ⚠ Everyone who tracks time has an address on file (Jesse, 2026-10-07) —
    // the server enforces it; this only says so before the round trip.
    if (form.tracksTime) {
      const required = 'Required for anyone who tracks time';
      if (!form.addressLine1.trim()) next.addressLine1 = required;
      if (!form.city.trim()) next.city = required;
      if (!form.country) next.country = required;
      if (form.country === 'US') {
        if (!form.region.trim()) next.region = 'State is required in the US';
        if (!form.postalCode.trim()) next.postalCode = 'ZIP is required in the US';
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  /**
   * Did anything the guarded PATCH owns actually change?
   *
   * Every field the generic route writes, compared against what was loaded.
   * If this is false the PATCH is not sent at all — which is what lets a
   * Harvest-owned person still be marked as a time tracker.
   */
  const guardedFieldsChanged = (current: Person, next: PersonFormValues): boolean =>
    current.name !== next.name ||
    current.email !== next.email ||
    current.type !== next.type ||
    current.costModel !== next.costModel ||
    current.hourlyCost !== next.hourlyCost ||
    current.flatMonthlyCost !== next.flatMonthlyCost ||
    current.defaultBillRate !== next.defaultBillRate ||
    current.weeklyCapacityHours !== next.weeklyCapacityHours ||
    (current.startDate ?? null) !== next.startDate ||
    (current.endDate ?? null) !== next.endDate;

  const submit = async () => {
    if (!validate()) return;
    setServerError(null);
    const values: PersonFormValues = {
      name: form.name.trim(),
      email: form.email.trim(),
      type: form.type as PersonFormValues['type'],
      costModel: form.costModel as PersonFormValues['costModel'],
      hourlyCost: parseMoney(form.hourlyCost),
      flatMonthlyCost: parseMoney(form.flatMonthlyCost),
      defaultBillRate: parseMoney(form.defaultBillRate),
      weeklyCapacityHours: form.weeklyCapacityHours.trim()
        ? Number(form.weeklyCapacityHours)
        : null,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
    };
    const policy = {
      tracksTime: form.tracksTime,
      timesheetCadence: form.tracksTime ? form.timesheetCadence : ('NEVER' as const),
      timesheetRequired: form.timesheetRequired,
    };
    try {
      /**
       * ⚠⚠ **THE GUARDED PATCH IS SKIPPED WHEN NOTHING GUARDED CHANGED, AND
       * THAT IS THE WHOLE POINT OF THIS ORDER.**
       *
       * `PATCH /team/:id` is refused with a 409 while Harvest owns the row —
       * correctly: it protects name, rates and type, which Harvest really does
       * own. On a live deployment with `syncMode: continuous`, EVERY person
       * imported from Harvest is such a row, so sending it unconditionally
       * threw before the time policy was ever reached (measured once already:
       * Carlos Sanchez, checkbox ticked, Save pressed, nothing saved).
       *
       * So the time policy goes through its own unguarded route
       * (`/time-policy`), and the guarded PATCH only runs when a guarded field
       * actually differs.
       */
      let saved = person;

      if (!person) {
        if (!member) return;
        const { name: _name, email: _email, ...rest } = values;
        void _name;
        void _email;
        saved = await create.mutateAsync({
          ...rest,
          authUserId: member.userId,
          ...policy,
          address: addressFrom(form),
        });
      } else {
        if (guardedFieldsChanged(person, values)) {
          saved = await update.mutateAsync(values);
        }
        // Before the time policy: turning tracking on is refused without it.
        const address = addressFrom(form);
        if (!sameAddress(address, person.address)) {
          saved = await setAddress.mutateAsync({ id: person.id, address });
        }
        if (
          person.tracksTime !== policy.tracksTime ||
          person.timesheetCadence !== policy.timesheetCadence ||
          person.timesheetRequired !== policy.timesheetRequired
        ) {
          saved = await setTimePolicy.mutateAsync({ id: person.id, ...policy });
        }
      }

      onOpenChange(false);
      if (saved) onSaved?.(saved);
    } catch (err) {
      setServerError(err instanceof ApiError ? err.message : 'Could not save — please try again');
    }
  };

  const toggleArchived = async () => {
    if (!person) return;
    try {
      const saved = await archive.mutateAsync({ id: person.id, archived: person.isActive });
      onOpenChange(false);
      onSaved?.(saved);
    } catch (err) {
      setServerError(err instanceof ApiError ? err.message : 'Could not update — please try again');
    }
  };

  const choosing = !person && !member;

  /** The one-line account fix, offered where the checkbox says it is needed. */
  const accountAction =
    person && !hasAccount ? (
      linking ? (
        <div className="rounded-lg border border-grey-200 bg-white p-2">
          <LinkAccountList
            person={person}
            enabled={open && linking}
            onLinked={() => {
              setLinkedHere(true);
              setLinking(false);
            }}
          />
          <Button variant="ghost" size="sm" className="mt-1" onClick={() => setLinking(false)} type="button">
            Cancel linking
          </Button>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setLinking(true)} type="button">
          <Link2 className="h-4 w-4" aria-hidden /> Link account
        </Button>
      )
    ) : null;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={person ? 'Edit person' : 'New person'}
      description={
        person
          ? 'Update role, cost model, rates or time tracking.'
          : choosing
            ? 'Pick their accounts.soliddigital.com account — everyone on the team has one.'
            : 'Set their role, cost and time tracking.'
      }
      size="lg"
      footer={
        choosing ? (
          <Button variant="outline" onClick={() => onOpenChange(false)} type="button">
            Cancel
          </Button>
        ) : (
          <>
            {person && (
              <Button
                variant="ghost"
                className="mr-auto text-grey-500"
                onClick={toggleArchived}
                loading={archive.isPending}
                type="button"
              >
                {person.isActive ? (
                  <>
                    <Archive className="h-4 w-4" aria-hidden /> Archive
                  </>
                ) : (
                  <>
                    <ArchiveRestore className="h-4 w-4" aria-hidden /> Restore
                  </>
                )}
              </Button>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)} type="button">
              Cancel
            </Button>
            <Button onClick={submit} loading={saving} type="button">
              {person ? 'Save changes' : 'Add person'}
            </Button>
          </>
        )
      }
    >
      {choosing ? (
        <AccountPicker
          enabled={open && choosing}
          onPick={pickMember}
          onLinked={(linked) => {
            onOpenChange(false);
            onSaved?.(linked);
          }}
        />
      ) : (
      <form
        className="grid grid-cols-1 gap-4 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {member ? (
          /* Name and email are the ACCOUNT's — the server re-reads them — so
             they are shown, not typed. "Change" goes back to the picker. */
          <div className="flex items-center justify-between gap-3 rounded-lg border border-grey-200 bg-grey-50 px-3 py-2.5 sm:col-span-2">
            <span className="flex min-w-0 items-center gap-3">
              <Avatar name={member.name} />
              <span className="min-w-0">
                <span className="block truncate font-semibold text-secondary">{member.name}</span>
                <span className="block truncate text-caption text-grey-500">
                  {member.email} · from their Solid account
                </span>
              </span>
            </span>
            <Button variant="ghost" size="sm" onClick={() => setMember(null)} type="button">
              Change
            </Button>
          </div>
        ) : (
          <>
            <Input
              label="Full name"
              value={form.name}
              onChange={(e) => set('name')(e.target.value)}
              error={errors.name}
              placeholder="Jordan Rivera"
              disabled={person?.hasSolidAccount}
              hint={person?.hasSolidAccount ? 'From their Solid account' : undefined}
            />
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => set('email')(e.target.value)}
              error={errors.email}
              placeholder="jordan@example.com"
              disabled={person?.hasSolidAccount}
              hint={person?.hasSolidAccount ? 'From their Solid account' : undefined}
            />
          </>
        )}
        <Select
          label="Type"
          value={form.type}
          onChange={(e) => set('type')(e.target.value)}
          options={[
            { value: 'STAFF', label: 'Staff' },
            { value: 'CONTRACTOR', label: 'Contractor' },
          ]}
        />
        <Select
          label="Cost model"
          value={form.costModel}
          onChange={(e) => set('costModel')(e.target.value)}
          options={[
            { value: 'HOURLY', label: 'Hourly cost' },
            { value: 'FLAT_MONTHLY', label: 'Flat monthly (salary)' },
          ]}
        />
        {/* Rates are a single current value, not an effective-dated history —
            say so where the damage happens rather than implying the change is
            forward-only. */}
        {person && (
          <p className="flex items-start gap-2 rounded-lg border border-status-partial-border bg-status-partial-bg px-3 py-2 text-caption text-secondary-800 sm:col-span-2">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-partial" aria-hidden />
            <span>
              Rates are not effective-dated yet: changing a cost or bill rate recomputes revenue,
              cost and margin for <strong className="font-semibold">every month</strong> in
              reporting, not only future ones.
            </span>
          </p>
        )}
        {form.costModel === 'HOURLY' ? (
          <Input
            label="Hourly cost"
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            icon={<DollarSign />}
            value={form.hourlyCost}
            onChange={(e) => set('hourlyCost')(e.target.value)}
            error={errors.hourlyCost}
            placeholder="85.00"
            hint="What an hour of their time costs you"
          />
        ) : (
          <Input
            label="Flat monthly cost"
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            icon={<DollarSign />}
            value={form.flatMonthlyCost}
            onChange={(e) => set('flatMonthlyCost')(e.target.value)}
            error={errors.flatMonthlyCost}
            placeholder="8500.00"
            hint="Salary + overhead per month"
          />
        )}
        <Input
          label="Default bill rate / hr"
          type="number"
          min={0}
          step="0.01"
          inputMode="decimal"
          icon={<DollarSign />}
          value={form.defaultBillRate}
          onChange={(e) => set('defaultBillRate')(e.target.value)}
          error={errors.defaultBillRate}
          placeholder="150.00"
          hint="Used when an agreement has no rate override"
        />
        {/* Full width: it is the utilization denominator, and it keeps the two
            date fields paired on their own row instead of orphaning one. */}
        <Input
          label="Weekly capacity (h)"
          type="number"
          min={0}
          max={168}
          step="0.5"
          inputMode="decimal"
          value={form.weeklyCapacityHours}
          onChange={(e) => set('weeklyCapacityHours')(e.target.value)}
          error={errors.weeklyCapacityHours}
          placeholder="40"
          className="sm:col-span-2"
          hint="Available hours per week — the denominator for utilization (billable ÷ capacity). Leave blank for on-demand people: they are shown as “no capacity set” and excluded from the utilization bands."
        />
        {/*
          ⚠ IT LIVES HERE BECAUSE THIS IS WHERE PEOPLE LOOK — every other person
          setting is in this form. It does NOT ride the same PATCH as the fields
          around it (see `submit()`): Harvest owns name/rates/type on a mirrored
          row and refuses writes to them; it has no notion of time tracking.
        */}
        <TimePolicyFields
          className="sm:col-span-2"
          type={form.type as PersonType}
          hasAccount={hasAccount}
          accountAction={accountAction}
          value={{
            tracksTime: form.tracksTime,
            timesheetCadence: form.timesheetCadence,
            timesheetRequired: form.timesheetRequired,
          }}
          onChange={(next) => setForm((f) => ({ ...f, ...next }))}
        />
        {/* Where they work from — required for anyone who tracks time, because
            the R&D tax credit counts US research only and the CPA needs it. */}
        <fieldset className="grid grid-cols-1 gap-4 sm:col-span-2 sm:grid-cols-2">
          <legend className="mb-1 text-body-sm font-semibold text-secondary">
            Address
            <span className="ml-2 font-normal text-grey-500">
              {form.tracksTime ? 'required — they track time' : 'required before they can track time'}
            </span>
          </legend>
          <Input
            label="Street address"
            value={form.addressLine1}
            onChange={(e) => set('addressLine1')(e.target.value)}
            error={errors.addressLine1}
            autoComplete="address-line1"
            className="sm:col-span-2"
          />
          <Input
            label="Address line 2"
            value={form.addressLine2}
            onChange={(e) => set('addressLine2')(e.target.value)}
            autoComplete="address-line2"
            className="sm:col-span-2"
          />
          <Input
            label="City"
            value={form.city}
            onChange={(e) => set('city')(e.target.value)}
            error={errors.city}
            autoComplete="address-level2"
          />
          <Input
            label={form.country === 'US' ? 'State' : 'State / region'}
            value={form.region}
            onChange={(e) => set('region')(e.target.value)}
            error={errors.region}
            autoComplete="address-level1"
          />
          <Input
            label={form.country === 'US' ? 'ZIP code' : 'Postal code'}
            value={form.postalCode}
            onChange={(e) => set('postalCode')(e.target.value)}
            error={errors.postalCode}
            autoComplete="postal-code"
          />
          <Select
            label="Country"
            value={form.country}
            onChange={(e) => set('country')(e.target.value)}
            error={errors.country}
            options={[{ value: '', label: 'Choose a country' }, ...countryOptions()]}
          />
        </fieldset>
        <Input
          label="Start date"
          type="date"
          value={form.startDate}
          onChange={(e) => set('startDate')(e.target.value)}
          hint="Capacity only counts from this date"
        />
        <Input
          label="End date"
          type="date"
          value={form.endDate}
          onChange={(e) => set('endDate')(e.target.value)}
          error={errors.endDate}
          hint="Leave blank while they're with you"
        />
        {serverError && (
          <p className="text-body-sm font-medium text-error sm:col-span-2" role="alert">
            {serverError}
          </p>
        )}
        {/* Hidden submit so Enter submits the form */}
        <button type="submit" className="hidden" aria-hidden />
      </form>
      )}
    </Modal>
  );
}
